import { useMediaQuery, useWindowScroll } from '@mantine/hooks'
import Loading from 'designs/Loading'
import ButtonLoading from 'designs/Loading/ButtonLoading'
import CatalogBannerRender from 'features/catalog/components/CatalogBannerRender'
import CatalogKDCBanner from 'features/catalog/components/CatalogBannerRender/CatalogKDCBanner'
import CatalogKlassCard from 'features/catalog/components/CatalogKlassCard'
import CatalogMenu from 'features/catalog/components/CatalogMenu'
import CatalogSortedButton from 'features/catalog/components/CatalogSortedButton'
import { useInfiniteKlassCategoryItemsListQuery } from 'features/catalog/queries/useKlassCategoryItemsListQuery'
import { useKlassCategoryListQuery } from 'features/catalog/queries/useKlassCategoryListQuery'
import { SortType } from 'features/catalog/types'
import { renderAiToast } from 'features/common/components/Toast/AiToast'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import WithHeader from 'shared/layout/WithHeader'

import styles from './Catalog.module.scss'

const CatalogDetail = () => {
  const router = useRouter()
  const isMobile = useMediaQuery('(max-width: 767px)')
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
  const { ref, inView } = useInView()
  const [scroll] = useWindowScroll()
  const isScrolling = isMobile ? scroll.y > 60 : scroll.y > 90

  const {
    data: klassCategoryList,
    isLoading: isLoadingKlassCategoryList,
  } = useKlassCategoryListQuery('catalog')

  const category = (router.query.category || 'all') as string
  const categoryName = useMemo(() => {
    if (klassCategoryList) {
      const name = klassCategoryList.find(item => item.category === category)?.name as string
      if (category === 'all') return `${name} 강의`
      return name
    }
  }, [category, klassCategoryList])

  const sort = (router.query.sort || '-order') as SortType
  const perPage = isMobile ? 6 : isTablet ? 9 : 12
  const isFromHomeAi = router.query.home

  useEffect(() => {
    if (isFromHomeAi) renderAiToast()
  }, [])

  const {
    data: klassCategoryItems,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteKlassCategoryItemsListQuery('catalog', category, sort, perPage)

  useEffect(() => {
    if (inView && hasNextPage) {
      void fetchNextPage()
    }
  }, [inView, hasNextPage, fetchNextPage])

  useEffect(() => {
    window.scrollTo(window.scrollX, 0)
  }, [])

  return (
    <WithHeader>
      {isLoadingKlassCategoryList && <Loading />}
      <div
        className="relative w-full pb-20 pt-6 md:pb-30 lg:pb-40 lg:pt-12"
        style={{ minWidth: 360 }}>
        {klassCategoryList && (
          <>
            <section className={`fixed z-100 w-full lg:sticky ${styles.menuContainer} `}>
              {isScrolling && (
                <div
                  data-aos="fade-down"
                  data-aos-easing="ease"
                  data-aos-duration="600"
                  data-aos-delay="300">
                  <CatalogMenu
                    menuType="button"
                    category={category}
                    klassCategoryList={klassCategoryList}
                    isScrolling={isScrolling}
                  />
                </div>
              )}
            </section>
            <section className="z-10 w-full ">
              <CatalogMenu
                menuType="imageButton"
                category={category}
                klassCategoryList={klassCategoryList}
                isScrolling={isScrolling}
              />
            </section>
          </>
        )}
        {/* 전체 배너 */}
        {/* {category === 'all' && (
          <section className="mx-auto mt-8 w-full max-w-7xl px-4 lg:mt-12 lg:px-6">
            <CatalogKDCBanner />
          </section>
        )} */}
        {/* kdc 배너 */}
        {/* {category === 'kdc' && (
          <section className="mx-auto mt-8 w-full max-w-7xl lg:mt-12">
            <CatalogBannerRender />
          </section>
        )} */}
        {/* 강의 목록 */}
        <section className="mx-auto w-full px-4 pt-8 lg:max-w-7xl lg:px-6 lg:pt-12">
          <header className="flex items-center justify-between">
            {klassCategoryList && (
              <h2 className="text-lg font-semibold md:text-2xl md:font-bold lg:text-4xl">
                {categoryName}
              </h2>
            )}
            <CatalogSortedButton sort={sort} />
          </header>
          {klassCategoryItems && (
            <main className="mt-4 lg:mt-6">
              {categoryName === '6주완성' ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <img
                    src="/svg/icon_people.svg"
                    alt="people icon"
                    className={`${
                      isMobile ? 'mb-4 h-[180px] w-[180px]' : 'mb-6 h-[250px] w-[250px]'
                    }`}
                  />
                  <p className="text-base font-normal leading-[1.63] text-neutral-400">
                    6주 완성 강의가 새로워 집니다.
                  </p>
                  <p className="mt-[6px] text-base font-normal leading-[1.63] text-neutral-400">
                    {isMobile
                      ? '더 알찬 강의로 다시 찾아뵙겠습니다.'
                      : '더 알찬 강의로 다시 찾아뵐 예정이니 기대해 주세요!'}
                  </p>
                  <div className="mt-8">
                    <button
                      onClick={() => {
                        void router.push({
                          pathname: '/catalog',
                          query: { category: 'all' },
                        })
                      }}
                      className="h-[44px] w-[220px] rounded-[8px] bg-yellow-450 text-white">
                      전체강의 둘러보기
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {klassCategoryItems.pages?.map((page, pageIdx) => (
                    <div
                      key={`$catalog_page_${pageIdx}`}
                      className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 first:mt-0 md:mt-12 md:grid-cols-3 md:gap-x-5 md:gap-y-12 lg:md:grid-cols-4 lg:gap-x-6">
                      {page.data.map((course, idx) => (
                        <CatalogKlassCard key={`${course.id}_catalog_${idx}`} course={course} />
                      ))}
                    </div>
                  ))}
                  <div ref={ref} />
                  {isFetchingNextPage && (
                    <div className="mt-4 flex justify-center">
                      <ButtonLoading color="text-yellow-450" />
                    </div>
                  )}
                </>
              )}
            </main>
          )}
        </section>
      </div>
    </WithHeader>
  )
}

export default CatalogDetail
